import { useEffect, useState } from "react";
import { Search, Bot, UserRound, MessageCircle, Smartphone } from "lucide-react";
import { listTags } from "@/lib/api";
import { avatarColor, dayjs, displayPhone, initials, tagColor } from "@/lib/format";

const FILTERS = [
  { value: "all", label: "Todas" },
  { value: "bot", label: "Bot" },
  { value: "needs_human", label: "Humano" },
  { value: "mine", label: "Mías" },
];

function timeLabel(ts) {
  const d = dayjs(ts);
  if (d.isSame(dayjs(), "day")) return d.format("HH:mm");
  if (d.isSame(dayjs().subtract(1, "day"), "day")) return "Ayer";
  return d.format("DD/MM/YY");
}

function Item({ c, active, onSelect }) {
  return (
    <button
      data-testid={`conversation-item-${c.id}`}
      onClick={() => onSelect(c.id)}
      className={`w-full text-left pl-3 pr-4 flex gap-3 items-center transition-colors ${active ? "bg-[#F0F2F5]" : "hover:bg-[#F5F6F6]"}`}
    >
      <div className={`h-12 w-12 shrink-0 rounded-full text-white font-bold flex items-center justify-center ${avatarColor(c.contact_wa_id)}`}>
        {initials(c.contact_name, c.contact_wa_id)}
      </div>
      <div className="flex-1 min-w-0 py-3 border-b border-slate-100">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[15px] font-semibold text-[#111B21] truncate">{c.contact_name || displayPhone(c.contact_wa_id)}</p>
          <span className={`text-xs shrink-0 ${c.unread_count ? "text-emerald-600 font-semibold" : "text-slate-400"}`}>{timeLabel(c.last_message_at)}</span>
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          {c.channel === "simulator" && <Smartphone className="h-3.5 w-3.5 text-slate-400 shrink-0" />}
          <p className="text-sm text-slate-500 truncate flex-1">{c.last_message_preview || "Sin mensajes"}</p>
          {c.unread_count > 0 && (
            <span data-testid={`unread-badge-${c.id}`} className="h-5 min-w-5 px-1.5 rounded-full bg-[#25D366] text-white text-[11px] font-bold flex items-center justify-center">{c.unread_count}</span>
          )}
        </div>
        <div className="mt-1.5 flex items-center gap-1 flex-wrap">
          {c.needs_human && (
            <span data-testid="needs-human-badge" className="inline-flex items-center gap-1 h-5 px-2 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800">
              <UserRound className="h-2.5 w-2.5" />Necesita humano
            </span>
          )}
          {c.bot_enabled && !c.needs_human && (
            <span className="inline-flex items-center gap-1 h-5 px-2 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700">
              <Bot className="h-2.5 w-2.5" />Bot
            </span>
          )}
          {!c.bot_enabled && !c.needs_human && c.assigned_agent_name && (
            <span className="h-5 px-2 rounded-full text-[10px] font-bold bg-sky-50 text-sky-700 inline-flex items-center">{c.assigned_agent_name.split(" ")[0]}</span>
          )}
          {(c.tags || []).slice(0, 3).map((t) => (
            <span key={t} className={`h-5 px-2 rounded-full text-[10px] font-bold inline-flex items-center ${tagColor(t)}`}>{t}</span>
          ))}
        </div>
      </div>
    </button>
  );
}

export default function ConversationList({
  conversations, activeId, onSelect, filter, onFilterChange, search, onSearch, tag, onTag, loading,
}) {
  const [tags, setTags] = useState([]);
  const tagKey = conversations.map((c) => (c.tags || []).join(",")).join("|");
  useEffect(() => { listTags().then(setTags).catch(() => {}); }, [tagKey]);

  return (
    <aside data-testid="conversation-list" className="w-full md:w-[360px] min-w-0 shrink-0 border-r border-slate-200 bg-white flex flex-col h-full">
      <div className="px-4 pt-5 pb-2">
        <h2 className="font-display text-2xl font-extrabold text-[#111B21]">Chats</h2>
        <div className="relative mt-3">
          <Search className="h-4 w-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="conversation-search-input" value={search} onChange={(e) => onSearch(e.target.value)}
            placeholder="Buscar nombre, número o mensaje"
            className="w-full h-9 rounded-lg bg-[#F0F2F5] pl-10 pr-3 text-sm outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>
        <div className="mt-3 flex gap-1.5 overflow-x-auto scroll-thin pb-1">
          {FILTERS.map((f) => (
            <button key={f.value} data-testid={`filter-tab-${f.value}`} onClick={() => onFilterChange(f.value)}
              className={`px-3 h-8 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${filter === f.value ? "bg-emerald-100 text-emerald-800" : "bg-[#F0F2F5] text-slate-600 hover:bg-slate-200"}`}>
              {f.label}
            </button>
          ))}
        </div>
        {tags.length > 0 && (
          <div className="mt-2 flex gap-1.5 overflow-x-auto scroll-thin pb-1" data-testid="tag-filter-bar">
            {tags.map((t) => (
              <button key={t} data-testid={`tag-filter-${t}`} onClick={() => onTag(tag === t ? "" : t)}
                className={`px-2.5 h-6 rounded-full text-[11px] font-bold whitespace-nowrap transition-[box-shadow,opacity] ${tagColor(t)} ${tag === t ? "ring-2 ring-offset-1 ring-slate-700" : "opacity-80 hover:opacity-100"}`}>
                #{t}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="flex-1 overflow-y-auto scroll-thin">
        {loading && <div className="p-6 text-sm text-slate-400">Cargando…</div>}
        {!loading && conversations.length === 0 && (
          <div className="p-10 text-center" data-testid="conversation-list-empty">
            <MessageCircle className="h-9 w-9 text-slate-300 mx-auto" />
            <p className="mt-3 text-sm font-semibold">No hay conversaciones</p>
            <p className="mt-1 text-xs text-slate-500">Los mensajes de WhatsApp y del simulador aparecerán aquí.</p>
          </div>
        )}
        {conversations.map((c) => <Item key={c.id} c={c} active={c.id === activeId} onSelect={onSelect} />)}
      </div>
    </aside>
  );
}
